// ./src/QrScanner/barcodeReader.ts
//
// The scanner's decoder (zxing-wasm), loaded ONLY when a scanner is first opened — its URL injected by
// the app the same way the icon registry is (`configureIcons`).
//
// ⚠ LAZY, BOTH HALVES (owner, 2026-09-29: "we do not need scan right away in most cases, but once loaded
// it can be cached"). Most sessions never open a camera, so neither the decoder's JavaScript (~40 KB) nor
// its WebAssembly (~0.95 MB) may ride in the main bundle or the service worker's install-time precache:
// - the JS module is a dynamic `import()` made the first time a scanner starts;
// - the `.wasm` is fetched at that same moment (`fireImmediately`, in parallel with the camera starting),
//   from the app's OWN URL — zxing-wasm would otherwise go to a public CDN — and the service worker then
//   serves it cache-first (`vite.config.ts` → runtimeCaching), so after the first scan it works offline.
// ⚠ The one trade-off, stated: the FIRST scan on a device needs a connection. A failed load is not
// remembered — the next scanner that opens tries again.
// `@em/ui` never imports the `.wasm` file itself, so nothing bundler-specific leaks into the package.
import type { ReaderOptions, ReadResult } from "zxing-wasm/reader";

/** The part of the zxing-wasm module the scanner uses. */
export interface BarcodeReader {
  readBarcodes: (input: ImageData, options?: ReaderOptions) => Promise<ReadResult[]>;
}

let configuredWasmUrl: string | null = null;
let loading: Promise<BarcodeReader> | null = null;

/** Call once at startup, before any scanner mounts. Stores the URL only — nothing is fetched here. */
export const configureBarcodeReader = (options: { wasmUrl: string }): void => {
  configuredWasmUrl = options.wasmUrl;
};

/** The URL configured by the app, or null (then zxing-wasm's own default applies). */
export const barcodeReaderWasmUrl = (): string | null => configuredWasmUrl;

/**
 * The decoder, loading it on first use. Every scanner shares one load; a FAILED load is forgotten
 * (and zxing-wasm's own half-built module purged) so the next attempt starts clean rather than
 * replaying the same rejection for the rest of the session.
 */
export const loadBarcodeReader = (): Promise<BarcodeReader> => {
  if (loading) {
    return loading;
  }

  loading = import("zxing-wasm/reader")
    .then(async (zxing) => {
      const wasmUrl = configuredWasmUrl;

      try {
        await zxing.prepareZXingModule({
          overrides: wasmUrl
            ? { locateFile: (path: string, prefix: string) => (path.endsWith(".wasm") ? wasmUrl : prefix + path) }
            : {},
          fireImmediately: true,
        });
      } catch (error) {
        zxing.purgeZXingModule();
        throw error;
      }

      return { readBarcodes: zxing.readBarcodes };
    })
    .catch((error) => {
      loading = null;
      throw error;
    });

  return loading;
};
